import React, { useEffect, useState, useMemo } from "react";
import { getAllGuard, getAllVip } from "../api/vipform";
import axios from "axios";
import { toast } from "react-toastify";

const BASE_URL = process.env.REACT_APP_BASE_URL;

export default function SelfRegistrations() {
  const [activeTab, setActiveTab] = useState("guard"); // guard | vip
  const [statusTab, setStatusTab] = useState("self"); // self | reject
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);

  const [searchText, setSearchText] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;

  const role = localStorage.getItem("role");
  const token =
    role === "admin"
      ? localStorage.getItem("adminToken")
      : localStorage.getItem("userToken");

  const axiosConfig = {
    headers: { Authorization: `Bearer ${token}` },
  };

  /* FETCH DATA */
  useEffect(() => {
    fetchSelfRegistrations();
  }, [activeTab]);

  const fetchSelfRegistrations = async () => {
    try {
      setLoading(true);
      let data = [];

      if (activeTab === "guard") {
        const res = await getAllGuard(0, 1000);
        data = res?.content || [];
      } else {
        const res = await getAllVip();
        data = Array.isArray(res) ? res : res?.data || [];
      }

      setList(data);
    } catch {
      toast.error("Failed to load registrations");
    } finally {
      setLoading(false);
    }
  };

  /* SEARCH */
  const searchedData = useMemo(() => {
    return list.filter((item) =>
      [item.name, item.email, item.contactno, item.rank, item.designation]
        .join(" ")
        .toLowerCase()
        .includes(searchText.toLowerCase())
    );
  }, [list, searchText]);

  /* STATUS FILTER */
  const statusFilteredData = useMemo(() => {
    return searchedData.filter((item) => {
      const s = String(item.status || "").toLowerCase();
      if (statusTab === "self") return s === "self";
      if (statusTab === "reject") return s === "rejected";
      return true;
    });
  }, [searchedData, statusTab]);

  /* PAGINATION */
  const totalPages = Math.ceil(statusFilteredData.length / rowsPerPage);
  const currentData = statusFilteredData.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  /* ACTIONS */
  const handleApprove = async (item) => {
    try {
      const { password, ...rest } = item;
      const payload = { ...rest, status: "Inactive" };

      if (activeTab === "guard") {
        await axios.put(
          `${BASE_URL}/api/officer/${item.id}/${role}`,
          payload,
          axiosConfig
        );
      } else {
        await axios.put(
          `${BASE_URL}/api/categories/${item.id}/${role}`,
          payload,
          axiosConfig
        );
      }

      toast.success("Approved successfully");
      setList((prev) =>
        prev.map((i) => (i.id === item.id ? { ...i, status: "Inactive" } : i))
      );
    } catch {
      toast.error("Approval failed");
    }
  };

  const handleReject = async (item) => {
    try {
      const payload = { ...item, status: "Rejected" };

      if (activeTab === "guard") {
        await axios.put(
          `${BASE_URL}/api/officer/${item.id}/reject`,
          payload,
          axiosConfig
        );
      } else {
        await axios.put(
          `${BASE_URL}/api/categories/${item.id}/reject`,
          payload,
          axiosConfig
        );
      }

      toast.success("Rejected successfully");
      setList((prev) =>
        prev.map((i) => (i.id === item.id ? { ...i, status: "Rejected" } : i))
      );
    } catch {
      toast.error("Rejection failed");
    }
  };

  /* PAGINATION WINDOW */
  const pageWindow = 2;
  const startPage = Math.max(1, currentPage - Math.floor(pageWindow / 2));
  const endPage = Math.min(totalPages, startPage + pageWindow);
  const visiblePages = Array.from(
    { length: endPage - startPage + 1 },
    (_, i) => startPage + i
  );

  return (
    <>
      <div className="selfreg-wrapper">
        {/* Header */}
        <header className="page-header">
          <h1 className="page-title">Self Registrations</h1>
          <div className="total-card">
            <span className="total-count">{statusFilteredData.length}</span>
            <span className="total-label">Pending / Rejected</span>
          </div>
        </header>

        {/* Tabs */}
        <section className="tabs-section">
          <div className="type-tabs">
            <button
              className={activeTab === "guard" ? "active" : ""}
              onClick={() => setActiveTab("guard")}
            >
              Officer
            </button>
            <button
              className={activeTab === "vip" ? "active" : ""}
              onClick={() => setActiveTab("vip")}
            >
              VIP
            </button>
          </div>

          <div className="status-tabs">
            <button
              className={statusTab === "self" ? "active" : ""}
              onClick={() => {
                setStatusTab("self");
                setCurrentPage(1);
              }}
            >
              Pending Approval
            </button>
            <button
              className={statusTab === "reject" ? "active" : ""}
              onClick={() => {
                setStatusTab("reject");
                setCurrentPage(1);
              }}
            >
              Rejected
            </button>
          </div>
        </section>

        {/* Search */}
        <section className="search-section">
          <div className="search-input-wrapper">
            <i className="fas fa-search"></i>
            <input
              type="text"
              placeholder="Search by name, email, contact..."
              value={searchText}
              onChange={(e) => {
                setSearchText(e.target.value);
                setCurrentPage(1);
              }}
            />
          </div>
        </section>

        {/* Table / Cards */}
        <section className="table-section">
          {loading ? (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Loading registrations...</p>
            </div>
          ) : currentData.length === 0 ? (
            <div className="empty-state">
              <i className="fas fa-user-clock"></i>
              <h3>No Registrations Found</h3>
              <p>No pending or rejected self-registrations at this time.</p>
            </div>
          ) : (
            <>
              {/* Desktop Table */}
              <div className="table-container desktop-only">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Contact</th>
                      <th>{activeTab === "guard" ? "Rank" : "Designation"}</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentData.map((item, index) => (
                      <tr key={item.id}>
                        <td>{index + 1 + (currentPage - 1) * rowsPerPage}</td>
                        <td className="name-cell">{item.name}</td>
                        <td>{item.email}</td>
                        <td>{item.contactno}</td>
                        <td>
                          <span className="badge">
                            {activeTab === "guard" ? item.rank : item.designation || "—"}
                          </span>
                        </td>
                        <td>
                          <span className={`status-badge ${item.status.toLowerCase()}`}>
                            {item.status === "self" ? "Pending" : item.status}
                          </span>
                        </td>
                        <td className="actions-cell">
                          {String(item.status).toLowerCase() === "self" && (
                            <>
                              <button
                                className="action-btn approve"
                                onClick={() => handleApprove(item)}
                              >
                                <i className="fas fa-check"></i> Approve
                              </button>
                              <button
                                className="action-btn reject"
                                onClick={() => handleReject(item)}
                              >
                                <i className="fas fa-times"></i> Reject
                              </button>
                            </>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Cards */}
              <div className="mobile-cards mobile-only">
                {currentData.map((item, index) => (
                  <div key={item.id} className="reg-card">
                    <div className="card-header">
                      <span className="index">#{index + 1 + (currentPage - 1) * rowsPerPage}</span>
                      <span className={`status-badge ${item.status.toLowerCase()}`}>
                        {item.status === "self" ? "Pending" : item.status}
                      </span>
                    </div>
                    <h3 className="reg-name">{item.name}</h3>
                    <div className="card-details">
                      <div><strong>Email:</strong> {item.email}</div>
                      <div><strong>Contact:</strong> {item.contactno}</div>
                      <div>
                        <strong>{activeTab === "guard" ? "Rank" : "Designation"}:</strong>{" "}
                        <span className="badge">
                          {activeTab === "guard" ? item.rank : item.designation || "—"}
                        </span>
                      </div>
                    </div>
                    {String(item.status).toLowerCase() === "self" && (
                      <div className="card-actions">
                        <button
                          className="action-btn approve"
                          onClick={() => handleApprove(item)}
                        >
                          <i className="fas fa-check"></i> Approve
                        </button>
                        <button
                          className="action-btn reject"
                          onClick={() => handleReject(item)}
                        >
                          <i className="fas fa-times"></i> Reject
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="pagination">
              <button
                onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                disabled={currentPage === 1}
                className="page-btn"
              >
                <i className="fas fa-chevron-left"></i>
              </button>

              {visiblePages.map((page) => (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  className={`page-btn ${currentPage === page ? "active" : ""}`}
                >
                  {page}
                </button>
              ))}

              <button
                onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="page-btn"
              >
                <i className="fas fa-chevron-right"></i>
              </button>
            </div>
          )}
        </section>
      </div>

      <style jsx>{`
        .selfreg-wrapper {
          min-height: 100vh;
          background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
          color: #e2e8f0;
          padding: 20px;
        }

        .page-header {
          margin-bottom: 30px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 20px;
        }

        .page-title {
          font-size: 2rem;
          font-weight: 700;
          color: #60a5fa;
        }

        .total-card {
          background: rgba(59, 130, 246, 0.2);
          border: 1px solid rgba(59, 130, 246, 0.4);
          padding: 16px 24px;
          border-radius: 16px;
          text-align: center;
        }

        .total-count {
          display: block;
          font-size: 1.8rem;
          font-weight: 800;
          color: #60a5fa;
        }

        .total-label {
          font-size: 0.95rem;
          opacity: 0.9;
        }

        .tabs-section {
          display: flex;
          flex-wrap: wrap;
          gap: 20px;
          margin-bottom: 30px;
          justify-content: space-between;
        }

        .type-tabs,
        .status-tabs {
          display: flex;
          gap: 12px;
          background: rgba(15, 23, 42, 0.6);
          padding: 8px;
          border-radius: 16px;
          border: 1px solid rgba(59, 130, 246, 0.4);
        }

        .type-tabs button,
        .status-tabs button {
          padding: 12px 24px;
          border-radius: 12px;
          background: transparent;
          color: #94a3b8;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .type-tabs button.active,
        .status-tabs button.active {
          background: #3b82f6;
          color: white;
        }

        .search-section {
          margin-bottom: 30px;
        }

        .search-input-wrapper {
          position: relative;
          max-width: 500px;
        }

        .search-input-wrapper i {
          position: absolute;
          left: 18px;
          top: 50%;
          transform: translateY(-50%);
          color: #94a3b8;
        }

        .search-input-wrapper input {
          width: 100%;
          padding: 16px 20px 16px 52px;
          background: rgba(15, 23, 42, 0.6);
          border: 2px solid rgba(59, 130, 246, 0.4);
          border-radius: 16px;
          color: white;
          font-size: 1rem;
        }

        .search-input-wrapper input:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.3);
        }

        .table-section {
          background: rgba(30, 41, 59, 0.8);
          backdrop-filter: blur(12px);
          border-radius: 20px;
          padding: 24px;
          border: 1px solid rgba(59, 130, 246, 0.3);
        }

        .loading-state,
        .empty-state {
          text-align: center;
          padding: 60px 20px;
          color: #94a3b8;
        }

        .spinner {
          width: 50px;
          height: 50px;
          border: 4px solid #1e293b;
          border-top: 4px solid #60a5fa;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin: 0 auto 20px;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .empty-state i {
          font-size: 4rem;
          opacity: 0.4;
          margin-bottom: 20px;
        }

        .empty-state h3 {
          font-size: 1.5rem;
          margin-bottom: 10px;
          color: white;
        }

        /* Desktop Table */
        .data-table {
          width: 100%;
          border-collapse: collapse;
        }

        .data-table th {
          background: rgba(59, 130, 246, 0.15);
          padding: 16px;
          text-align: left;
          font-weight: 600;
          color: #60a5fa;
          font-size: 0.95rem;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .data-table tr {
          transition: all 0.3s ease;
        }

        .data-table tr:hover {
          background: rgba(59, 130, 246, 0.1);
        }

        .data-table td {
          padding: 18px 16px;
          border-bottom: 1px solid rgba(100, 116, 139, 0.3);
        }

        .name-cell {
          font-weight: 600;
          color: white;
        }

        .badge {
          background: rgba(59, 130, 246, 0.2);
          color: #60a5fa;
          padding: 8px 16px;
          border-radius: 20px;
          font-weight: 600;
          font-size: 0.9rem;
        }

        .status-badge {
          padding: 8px 16px;
          border-radius: 20px;
          font-weight: 600;
          font-size: 0.9rem;
        }

        .status-badge.self {
          background: rgba(255, 193, 7, 0.2);
          color: #f59e0b;
        }

        .status-badge.rejected {
          background: rgba(239, 68, 68, 0.2);
          color: #ef4444;
        }

        .actions-cell {
          display: flex;
          gap: 12px;
        }

        .action-btn {
          padding: 10px 16px;
          border: none;
          border-radius: 10px;
          cursor: pointer;
          font-weight: 600;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .action-btn.approve {
          background: #10b981;
          color: white;
        }

        .action-btn.approve:hover {
          background: #059669;
          transform: translateY(-2px);
        }

        .action-btn.reject {
          background: #ef4444;
          color: white;
        }

        .action-btn.reject:hover {
          background: #dc2626;
          transform: translateY(-2px);
        }

        /* Mobile Cards */
        .mobile-cards {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .reg-card {
          background: rgba(15, 23, 42, 0.6);
          border: 1px solid rgba(59, 130, 246, 0.3);
          border-radius: 16px;
          padding: 20px;
          transition: all 0.3s ease;
        }

        .card-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 12px;
        }

        .index {
          font-weight: 600;
          color: #60a5fa;
        }

        .reg-name {
          font-size: 1.3rem;
          font-weight: 700;
          color: white;
          margin-bottom: 12px;
        }

        .card-details {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 10px;
          margin-bottom: 16px;
          font-size: 0.95rem;
        }

        .card-details div strong {
          color: #60a5fa;
        }

        .card-actions {
          display: flex;
          gap: 12px;
          margin-top: 16px;
        }

        /* Pagination */
        .pagination {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 12px;
          margin-top: 30px;
          flex-wrap: wrap;
        }

        .page-btn {
          width: 44px;
          height: 44px;
          background: rgba(15, 23, 42, 0.6);
          border: 1px solid rgba(59, 130, 246, 0.4);
          border-radius: 12px;
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .page-btn:hover:not(:disabled) {
          background: #3b82f6;
          transform: translateY(-2px);
        }

        .page-btn.active {
          background: #3b82f6;
          border-color: #3b82f6;
          font-weight: 600;
        }

        .page-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        /* Responsive */
        @media (max-width: 991px) {
          .desktop-only { display: none; }
          .mobile-only { display: flex; }
          .card-details { grid-template-columns: 1fr; }
        }

        @media (min-width: 992px) {
          .mobile-only { display: none; }
          .desktop-only { display: block; }
        }

        @media (max-width: 768px) {
          .page-header {
            flex-direction: column;
            align-items: flex-start;
          }

          .tabs-section {
            flex-direction: column;
            align-items: stretch;
          }

          .type-tabs,
          .status-tabs {
            justify-content: center;
          }

          .page-title {
            font-size: 1.8rem;
          }
        }
      `}</style>
    </>
  );
}


// import React, { useEffect, useState, useMemo } from "react";
// import { getAllGuard, getAllVip } from "../api/vipform";
// import axios from "axios";
// import { toast } from "react-toastify";

// const BASE_URL = process.env.REACT_APP_BASE_URL;

// export default function SelfRegistrations() {
//   const [activeTab, setActiveTab] = useState("guard"); // guard | vip
//   const [statusTab, setStatusTab] = useState("self"); // self | approve | reject
//   const [list, setList] = useState([]);
//   const [loading, setLoading] = useState(false);

//   const [searchText, setSearchText] = useState("");
//   const [currentPage, setCurrentPage] = useState(1);
//   const rowsPerPage = 10;

//   const role = localStorage.getItem("role");
//   const token =
//     role === "admin"
//       ? localStorage.getItem("adminToken")
//       : localStorage.getItem("userToken");

//   const axiosConfig = {
//     headers: { Authorization: `Bearer ${token}` },
//   };

//   /* ---------------- FETCH DATA ---------------- */
//   useEffect(() => {
//     fetchSelfRegistrations();
//     // eslint-disable-next-line
//   }, [activeTab]);

//   const fetchSelfRegistrations = async () => {
//     try {
//       setLoading(true);
//       let data = [];

//       if (activeTab === "guard") {
//         const res = await getAllGuard(0, 1000);
//         data = res?.content || [];
//       } else {
//         const res = await getAllVip();
//         data = Array.isArray(res) ? res : res?.data || [];
//       }

//       setList(data);
//     } catch {
//       toast.error("Failed to load registrations");
//     } finally {
//       setLoading(false);
//     }
//   };

//   /* ---------------- SEARCH ---------------- */
//   const searchedData = useMemo(() => {
//     return list.filter((item) =>
//       [
//         item.name,
//         item.email,
//         item.contactno,
//         item.rank,
//         item.designation,
//       ]
//         .join(" ")
//         .toLowerCase()
//         .includes(searchText.toLowerCase())
//     );
//   }, [list, searchText]);

//   /* ---------------- STATUS FILTER ---------------- */
//   const statusFilteredData = useMemo(() => {
//     return searchedData.filter((item) => {
//       const s = String(item.status || "").toLowerCase();
//       if (statusTab === "self") return s === "self";
//       if (statusTab === "approve") return s === "inactive";
//       if (statusTab === "reject") return s === "rejected";
//       return true;
//     });
//   }, [searchedData, statusTab]);

//   /* ---------------- PAGINATION DATA ---------------- */
//   const totalPages = Math.ceil(statusFilteredData.length / rowsPerPage);

//   const currentData = statusFilteredData.slice(
//     (currentPage - 1) * rowsPerPage,
//     currentPage * rowsPerPage
//   );

//   /* ---------------- ACTIONS ---------------- */
//   const handleApprove = async (item) => {
//   try {
//     // ❌ remove password from payload
//     const { password, ...rest } = item;

//     const payload = {
//       ...rest,
//       status: "Inactive",
//     };

//     if (activeTab === "guard") {
//       await axios.put(
//         `${BASE_URL}/api/officer/${item.id}/${role}`,
//         payload,
//         axiosConfig
//       );
//     } else {
//       await axios.put(
//         `${BASE_URL}/api/categories/${item.id}/${role}`,
//         payload,
//         axiosConfig
//       );
//     }

//     toast.success("Approved successfully");

//     setList((prev) =>
//       prev.map((i) =>
//         i.id === item.id ? { ...i, status: "Inactive" } : i
//       )
//     );
//   } catch {
//     toast.error("Approval failed");
//   }
// };

//   const handleReject = async (item) => {
//     try {
//       const payload = { ...item, status: "Rejected" };

//       if (activeTab === "guard") {
//         await axios.put(
//           `${BASE_URL}/api/officer/${item.id}/reject`,
//           payload,
//           axiosConfig
//         );
//       } else {
//         await axios.put(
//           `${BASE_URL}/api/categories/${item.id}/reject`,
//           payload,
//           axiosConfig
//         );
//       }

//       toast.success("Rejected successfully");

//       setList((prev) =>
//         prev.map((i) =>
//           i.id === item.id ? { ...i, status: "Rejected" } : i
//         )
//       );
//     } catch {
//       toast.error("Rejection failed");
//     }
//   };

//   return (
//     <div style={styles.page}>
//       {/* SEARCH */}
//       <input
//         placeholder="Search..."
//         value={searchText}
//         onChange={(e) => {
//           setSearchText(e.target.value);
//           setCurrentPage(1);
//         }}
//         style={styles.searchInput}
//       />

//       {/* STATUS + TYPE */}
//       <div style={styles.switchRow}>
//         <div style={styles.tabRow}>
//           {/* {["self", "approve", "reject"].map((s) => ( */}
//           {["self",  "reject"].map((s) => (
//             <button
//               key={s}
//               style={{
//                 ...styles.tabBtn,
//                 ...(statusTab === s ? styles.activeTab : {}),
//               }}
//               onClick={() => {
//                 setStatusTab(s);
//                 setCurrentPage(1);
//               }}
//             >
//               {s.toUpperCase()}
//             </button>
//           ))}
//         </div>

//         <div style={styles.tabRow}>
//           <button
//             style={{
//               ...styles.tabBtn,
//               ...(activeTab === "guard" ? styles.activeTab : {}),
//             }}
//             onClick={() => setActiveTab("guard")}
//           >
//             Officer
//           </button>
//           <button
//             style={{
//               ...styles.tabBtn,
//               ...(activeTab === "vip" ? styles.activeTab : {}),
//             }}
//             onClick={() => setActiveTab("vip")}
//           >
//             VIP
//           </button>
//         </div>
//       </div>

//       {/* TABLE */}
//       <div style={styles.tableContainer}>
//         {loading ? (
//           <div style={styles.noData}>Loading...</div>
//         ) : currentData.length === 0 ? (
//           <div style={styles.noData}>No Data Found</div>
//         ) : (
//           <table style={styles.table}>
//             <thead>
//               <tr>
//                 <th style={styles.th}>#</th>
//                 <th style={styles.th}>Name</th>
//                 <th style={styles.th}>Email</th>
//                 <th style={styles.th}>Contact</th>
//                 <th style={styles.th}>
//                   {activeTab === "guard" ? "Rank" : "Designation"}
//                 </th>
//                 <th style={styles.th}>Status</th>
//                 <th style={styles.th}>Action</th>
//               </tr>
//             </thead>
//             <tbody>
//               {currentData.map((item, index) => (
//                 <tr key={item.id}>
//                   <td style={styles.td}>
//                     {index + 1 + (currentPage - 1) * rowsPerPage}
//                   </td>
//                   <td style={styles.td}>{item.name}</td>
//                   <td style={styles.td}>{item.email}</td>
//                   <td style={styles.td}>{item.contactno}</td>
//                   <td style={styles.td}>
//                     {activeTab === "guard"
//                       ? item.rank
//                       : item.designation}
//                   </td>
//                   <td style={styles.td}>{item.status}</td>
//                   <td style={styles.td}>
//                     {String(item.status).toLowerCase() === "self" && (
//                       <>
//                         <button
//                           style={styles.approveBtn}
//                           onClick={() => handleApprove(item)}
//                         >
//                           Approve
//                         </button>
//                         <button
//                           style={styles.rejectBtn}
//                           onClick={() => handleReject(item)}
//                         >
//                           Reject
//                         </button>
//                       </>
//                     )}
//                   </td>
//                 </tr>
//               ))}
//             </tbody>
//           </table>
//         )}
//       </div>

//       {/* PAGINATION (CENTER + 2 NUMBERS) */}
//       {totalPages > 1 && (
//         <div style={styles.paginationWrapper}>
//           <button
//             style={styles.pageBtn}
//             disabled={currentPage === 1}
//             onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
//           >
//             Back
//           </button>

//           {Array.from({ length: totalPages })
//             .slice(
//               Math.max(currentPage - 1, 0),
//               Math.min(currentPage + 1, totalPages)
//             )
//             .map((_, idx) => {
//               const pageNumber =
//                 Math.max(currentPage - 1, 1) + idx;

//               return (
//                 <button
//                   key={pageNumber}
//                   style={{
//                     ...styles.pageBtn,
//                     ...(currentPage === pageNumber
//                       ? styles.activeTab
//                       : {}),
//                   }}
//                   onClick={() => setCurrentPage(pageNumber)}
//                 >
//                   {pageNumber}
//                 </button>
//               );
//             })}

//           <button
//             style={styles.pageBtn}
//             disabled={currentPage === totalPages}
//             onClick={() =>
//               setCurrentPage((p) => Math.min(p + 1, totalPages))
//             }
//           >
//             Next
//           </button>
//         </div>
//       )}
//     </div>
//   );
// }

// /* ---------------- STYLES ---------------- */
// const styles = {
//   page: {

//     padding: 40,
//     minHeight: "100vh",
//     background:
//       "linear-gradient(135deg, rgba(145,195,243,0.7), rgba(233,239,125,0.9))",
//   },
//   searchInput: {
//     width: "100%",
//     padding: 10,
//     borderRadius: 10,
//     border: "1.5px solid #1967d2",
//     marginBottom: 10,
//   },
//   switchRow: {
//     display: "flex",
//     justifyContent: "space-between",
//     marginBottom: 10,
//     flexWrap: "wrap",
//     gap: 10,
//   },
//   tabRow: { display: "flex", gap: 8 },
//   tabBtn: {
//     padding: "8px 16px",
//     borderRadius: 8,
//     border: "1.5px solid #1967d2",
//     background: "#fff",
//     cursor: "pointer",
//     fontWeight: 600,
//   },
//   activeTab: { background: "#1967d2", color: "#fff" },
//   tableContainer: {
//     background: "rgba(255,255,255,0.9)",
//     borderRadius: 14,
//     boxShadow: "0 8px 25px rgba(0,0,0,0.15)",
//     overflowX: "auto",
//   },
//   table: { width: "100%", borderCollapse: "collapse" },
//   th: {
//     padding: 12,
//     background: "#b8b8f3ff",
//     textAlign: "left",
//   },
//   td: { padding: 12, borderBottom: "1px solid #ddd" },
//   approveBtn: {
//     background: "#28a745",
//     color: "#fff",
//     border: 0,
//     padding: "5px 10px",
//     marginRight: 5,
//   },
//   rejectBtn: {
//     background: "#fc4f30",
//     color: "#fff",
//     border: 0,
//     padding: "5px 10px",
//   },
//   paginationWrapper: {
//     marginTop: 16,
//     display: "flex",
//     justifyContent: "center",
//     alignItems: "center",
//     gap: 8,
//   },
//   pageBtn: {
//     padding: "6px 14px",
//     borderRadius: 8,
//     border: "1.5px solid #1967d2",
//     background: "#fff",
//     cursor: "pointer",
//     fontWeight: 600,
//   },
//   noData: { padding: 20, textAlign: "center" },
// };


// //-----------------------------------1---------------------------

// // import React, { useEffect, useState, useMemo } from "react";
// // import { getAllGuard, getAllVip } from "../api/vipform";
// // import axios from "axios";
// // import { toast } from "react-toastify";

// // const BASE_URL = process.env.REACT_APP_BASE_URL;


// // export default function SelfRegistrations() {
// //   const [activeTab, setActiveTab] = useState("guard");
// //   const [list, setList] = useState([]);
// //   const [loading, setLoading] = useState(false);

// //   const [searchText, setSearchText] = useState("");
// //   const [currentPage, setCurrentPage] = useState(1);
// //   const rowsPerPage = 10;

// //   const role = localStorage.getItem("role");
// //   const token =
// //     role === "admin"
// //       ? localStorage.getItem("adminToken")
// //       : localStorage.getItem("userToken");
// //   console.log("tokeen", token, role)
// //   /* ---------------- FETCH DATA ---------------- */
// //   useEffect(() => {
// //     fetchSelfRegistrations();
// //     // eslint-disable-next-line
// //   }, [activeTab]);

// //   const fetchSelfRegistrations = async () => {
// //     try {
// //       setLoading(true);
// //       let data = [];

// //       if (activeTab === "guard") {
// //         const res = await getAllGuard(0, 1000);
// //         data = res?.content || [];
// //       } else {
// //         const res = await getAllVip();
// //         data = Array.isArray(res) ? res : res?.data || [];
// //       }

// //       const selfOnly = data.filter(
// //         (item) => String(item.status || "").toLowerCase() === "self"
// //       );

// //       setList(selfOnly);
// //     } catch (err) {
// //       console.error(err);
// //       toast.error("Failed to load self registrations");
// //     } finally {
// //       setLoading(false);
// //     }
// //   };

// //   /* ---------------- SEARCH ---------------- */
// //   const filteredData = useMemo(() => {
// //     return list.filter((item) =>
// //       [
// //         item.name,
// //         item.email,
// //         item.contactno,
// //         item.rank,
// //         item.designation,
// //       ]
// //         .join(" ")
// //         .toLowerCase()
// //         .includes(searchText.toLowerCase())
// //     );
// //   }, [list, searchText]);

// //   /* ---------------- PAGINATION ---------------- */
// //   const totalPages = Math.ceil(filteredData.length / rowsPerPage);

// //   const currentData = filteredData.slice(
// //     (currentPage - 1) * rowsPerPage,
// //     currentPage * rowsPerPage
// //   );
// //   const axiosConfig = {
// //     headers: {
// //       Authorization: `Bearer ${token}`,
// //     },
// //   };

// //   /* ---------------- ACTIONS ---------------- */
// //   const handleApprove = async (item) => {
// //     try {
// //       const role = localStorage.getItem("role");

// //       const payload = {
// //         ...item,
// //         status: "Inactive",
// //       };

// //       if (activeTab === "guard") {
// //         await axios.put(
// //           `${BASE_URL}/api/officer/${item.id}/${role}`,
// //           payload,
// //           axiosConfig
// //         );
// //       } else {
// //         await axios.put(
// //           `${BASE_URL}/api/categories/${item.id}/${role}`,
// //           payload,
// //           axiosConfig
// //         );
// //       }

// //       toast.success("Approved successfully");

// //       setList((prev) => prev.filter((i) => i.id !== item.id));
// //     } catch (error) {
// //       console.error(error);
// //       toast.error("Approval failed");
// //     }
// //   };

// //   const handleReject = async (item) => {
// //     try {
// //       const payload = {
// //         ...item,
// //         status: "Rejected",
// //       };

// //       if (activeTab === "guard") {
// //         await axios.put(
// //           `${BASE_URL}/api/officer/${item.id}/reject`,
// //           payload,
// //           axiosConfig

// //         );
// //       } else {
// //         await axios.put(
// //           `${BASE_URL}/api/categories/${item.id}/reject`,
// //           payload,
// //           axiosConfig

// //         );
// //       }

// //       toast.success("Rejected successfully");
// //       setList((prev) => prev.filter((i) => i.id !== item.id));
// //     } catch (error) {
// //       console.error(error);
// //       toast.error("Rejection failed");
// //     }
// //   };


// //   return (
// //     <div style={styles.page}>
// //       {/* HEADER */}
// //       <div style={styles.headerRow}>
// //         <h2 style={styles.title}>Self Registrations</h2>

// //         <div style={styles.tabRow}>
// //           <button
// //             style={{
// //               ...styles.tabBtn,
// //               ...(activeTab === "guard" ? styles.activeTab : {}),
// //             }}
// //             onClick={() => {
// //               setActiveTab("guard");
// //               setCurrentPage(1);
// //             }}
// //           >
// //             Guard
// //           </button>
// //           <button
// //             style={{
// //               ...styles.tabBtn,
// //               ...(activeTab === "vip" ? styles.activeTab : {}),
// //             }}
// //             onClick={() => {
// //               setActiveTab("vip");
// //               setCurrentPage(1);
// //             }}
// //           >
// //             VIP
// //           </button>
// //         </div>
// //       </div>

// //       {/* SEARCH */}
// //       <div style={styles.topBar}>
// //         <input
// //           placeholder="Search name, email, contact, rank/designation..."
// //           value={searchText}
// //           onChange={(e) => {
// //             setSearchText(e.target.value);
// //             setCurrentPage(1);
// //           }}
// //           style={styles.searchInput}
// //         />
// //         <div style={styles.countBox}>
// //           Total: <b>{filteredData.length}</b>
// //         </div>
// //       </div>

// //       {/* TABLE */}
// //       <div style={styles.tableOuter}>
// //         <div style={styles.tableContainer}>
// //           {loading ? (
// //             <div style={styles.noData}>Loading...</div>
// //           ) : currentData.length === 0 ? (
// //             <div style={styles.noData}>No Self Registrations Found</div>
// //           ) : (
// //             <table style={styles.table}>
// //               <thead>
// //                 <tr>
// //                   <th style={styles.th}>#</th>
// //                   <th style={styles.th}>Name</th>
// //                   <th style={styles.th}>Email</th>
// //                   <th style={styles.th}>Contact</th>
// //                   <th style={styles.th}>
// //                     {activeTab === "guard" ? "Rank" : "Designation"}
// //                   </th>
// //                   <th style={styles.th}>Status</th>
// //                   <th style={styles.th}>Actions</th>
// //                 </tr>
// //               </thead>

// //               <tbody>
// //                 {currentData.map((item, index) => (
// //                   <tr
// //                     key={item.id}
// //                     style={{
// //                       ...styles.row,
// //                       ...(index % 2 === 0 ? styles.evenRow : {}),
// //                     }}
// //                   >
// //                     <td style={styles.td}>
// //                       {index + 1 + (currentPage - 1) * rowsPerPage}
// //                     </td>
// //                     <td style={styles.td}>{item.name}</td>
// //                     <td style={styles.td}>{item.email}</td>
// //                     <td style={styles.td}>{item.contactno}</td>
// //                     <td style={styles.td}>
// //                       <span style={styles.badge}>
// //                         {activeTab === "guard"
// //                           ? item.rank
// //                           : item.designation}
// //                       </span>
// //                     </td>
// //                     <td style={styles.td}>
// //                       <span style={styles.statusSelf}>Self</span>
// //                     </td>
// //                     <td style={styles.td}>
// //                       <div style={styles.actionCol}>
// //                         <button
// //                           style={styles.approveBtn}
// //                           onClick={() => handleApprove(item)}
// //                         >
// //                           Approve
// //                         </button>

// //                         <button
// //                           style={styles.rejectBtn}
// //                           onClick={() => handleReject(item)}
// //                         >
// //                           Reject
// //                         </button>
// //                       </div>
// //                     </td>
// //                   </tr>
// //                 ))}
// //               </tbody>
// //             </table>
// //           )}
// //         </div>
// //       </div>

// //       {/* PAGINATION */}
// //       {totalPages > 1 && (
// //         <div style={styles.pagination}>
// //           <button
// //             disabled={currentPage === 1}
// //             style={styles.pageBtn}
// //             onClick={() => setCurrentPage((p) => p - 1)}
// //           >
// //             Prev
// //           </button>

// //           {[...Array(totalPages)].map((_, i) => (
// //             <button
// //               key={i}
// //               style={{
// //                 ...styles.pageBtn,
// //                 ...(currentPage === i + 1 ? styles.activePage : {}),
// //               }}
// //               onClick={() => setCurrentPage(i + 1)}
// //             >
// //               {i + 1}
// //             </button>
// //           ))}

// //           <button
// //             disabled={currentPage === totalPages}
// //             style={styles.pageBtn}
// //             onClick={() => setCurrentPage((p) => p + 1)}
// //           >
// //             Next
// //           </button>
// //         </div>
// //       )}
// //     </div>
// //   );
// // }

// // /* ---------------- STYLES ---------------- */
// // const styles = {
// //   page: {
// //     padding: 16,
// //     minHeight: "100vh",
// //     background:
// //       "linear-gradient(135deg, rgba(145,195,243,0.7), rgba(233,239,125,0.9))",
// //   },

// //   headerRow: {
// //     display: "flex",
// //     justifyContent: "space-between",
// //     flexWrap: "wrap",
// //     gap: 12,
// //     marginBottom: 16,
// //   },

// //   title: { fontSize: 26, fontWeight: 800, color: "#1967d2" },

// //   tabRow: { display: "flex", gap: 8 },

// //   tabBtn: {
// //     padding: "8px 18px",
// //     borderRadius: 8,
// //     border: "1.5px solid #1967d2",
// //     background: "#fff",
// //     cursor: "pointer",
// //     fontWeight: 600,
// //   },

// //   activeTab: { background: "#1967d2", color: "#fff" },

// //   topBar: {
// //     display: "flex",
// //     justifyContent: "space-between",
// //     flexWrap: "wrap",
// //     gap: 12,
// //     marginBottom: 12,
// //   },

// //   searchInput: {
// //     flex: "1 1 300px",
// //     padding: "10px 14px",
// //     borderRadius: 10,
// //     border: "1.5px solid #1967d2",
// //   },

// //   countBox: {
// //     background: "#fff",
// //     padding: "10px 14px",
// //     borderRadius: 10,
// //     fontWeight: 600,
// //   },

// //   tableOuter: { width: "100%", overflowX: "auto" },

// //   tableContainer: {
// //     minWidth: 950,
// //     maxHeight: "70vh",
// //     overflowY: "auto",
// //     background: "rgba(255,255,255,0.9)",
// //     borderRadius: 14,
// //     boxShadow: "0 8px 25px rgba(0,0,0,0.15)",
// //   },

// //   table: {
// //     width: "100%",
// //     borderCollapse: "collapse",
// //   },

// //   th: {
// //     padding: "14px 12px",
// //     textAlign: "left",
// //     fontWeight: 700,
// //     background: "#b8b8f3ff",
// //     borderBottom: "2px solid #d0d7e2",
// //     position: "sticky",
// //     top: 0,
// //     zIndex: 1,
// //   },

// //   td: {
// //     padding: "14px 12px",
// //     borderBottom: "1px solid #e3e8f0",
// //     fontSize: 14,
// //   },

// //   row: {
// //     height: 56,
// //     transition: "background 0.2s ease",
// //   },

// //   evenRow: {
// //     background: "#fafafa",
// //   },

// //   badge: {
// //     background: "#d7ecff",
// //     padding: "5px 12px",
// //     borderRadius: 20,
// //     fontWeight: 600,
// //     fontSize: 13,
// //   },

// //   statusSelf: {
// //     background: "#fff3cd",
// //     color: "#856404",
// //     padding: "5px 12px",
// //     borderRadius: 20,
// //     fontWeight: 600,
// //     fontSize: 13,
// //   },

// //   actionCol: { display: "flex", gap: 8, flexWrap: "wrap" },

// //   approveBtn: {
// //     background: "#28a745",
// //     color: "#fff",
// //     border: 0,
// //     padding: "6px 14px",
// //     borderRadius: 6,
// //     cursor: "pointer",
// //   },

// //   rejectBtn: {
// //     background: "#fc4f30",
// //     color: "#fff",
// //     border: 0,
// //     padding: "6px 14px",
// //     borderRadius: 6,
// //     cursor: "pointer",
// //   },

// //   pagination: {
// //     display: "flex",
// //     justifyContent: "center",
// //     gap: 8,
// //     marginTop: 16,
// //     flexWrap: "wrap",
// //   },

// //   pageBtn: {
// //     padding: "6px 14px",
// //     borderRadius: 6,
// //     border: "1px solid #1967d2",
// //     background: "#fff",
// //     cursor: "pointer",
// //   },

// //   activePage: {
// //     background: "#1967d2",
// //     color: "#fff",
// //     fontWeight: 700,
// //   },

// //   noData: {
// //     textAlign: "center",
// //     padding: 30,
// //     fontSize: 16,
// //     color: "#666",
// //   },
// // };
